import { Drawer, message, Modal } from 'antd';
import BigNumber from 'bignumber.js';
import { Check, Copy, XIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { ellipseAddress } from 'src/helpers';
import { removeAuthTokens } from 'src/helpers/storage';
import useWindowSize from 'src/hooks/useWindowSize';
import { useAppTranslation } from 'src/i18n/hooks';
import { useAuth } from 'src/libs/rainbow';
import { queryClient } from 'src/libs/react-query';
import { userKeys } from 'src/services/user/user-keys';
import { emojiAvatarForAddress } from 'src/utils/emojiAvatarForAddress';
import { Address } from 'viem';
import { useBalance, useDisconnect } from 'wagmi';
import { useClipboard } from 'use-clipboard-copy';

import './styles.scss';
import { useGetAllTimeLeaderboard } from 'src/services/leaderboard/get-all-time-leaderboard';
import { getTierImage } from 'src/pages/trading-leaderboard';
import { useAppDispatch } from 'src/store';
import { clearUserInfo } from 'src/store/actions/user';
import { useWalletSelector } from '@near-wallet-selector/react-hook';

interface Props {
  open: boolean;
  onClose: () => void;
  walletAddress?: string;
}

const WalletInformationModal = ({ open, onClose, walletAddress }: Props) => {
  const userTheme = useSelector((state: any) => state.theme);
  const [copyApiBlocked, setCopyApiBlocked] = useState(false);
  const { isMobile, isTablet } = useWindowSize();
  const { disconnectAsync } = useDisconnect();
  const { signOut } = useWalletSelector();
  const dispatch = useAppDispatch();
  const { t } = useAppTranslation(['common']);

  const { setAuthStatus, isEthereumWallet } = useAuth();
  const { copy, copied } = useClipboard({
    copiedTimeout: 1000,
    onError: () => {
      message.error(t('wallet.copyApiBlockedWarning'));
      setCopyApiBlocked(true);
    },
  });

  const { data: AllTimeLeaderboardData } = useGetAllTimeLeaderboard(
    {
      page: 1,
      page_size: 10,
      useraddress: walletAddress,
    },
    {
      enabled: !!walletAddress,
    },
  );

  const { emoji, color } = useMemo(
    () => emojiAvatarForAddress(walletAddress ?? ''),
    [walletAddress],
  );

  const { data } = useBalance({
    address: walletAddress as Address,
  });
  const handleCopyAddress = async () => {
    copy(walletAddress);
  };

  const handleDisconnect = async () => {
    removeAuthTokens();
    setAuthStatus('unauthenticated');
    dispatch(clearUserInfo());
    onClose?.();
    try {
      if (isEthereumWallet) {
        await disconnectAsync();
      } else {
        await signOut();
      }
    } catch {}
    onClose?.();
    await queryClient.resetQueries({
      queryKey: userKeys.all,
    });
  };

  return (
    <>
      {isMobile || isTablet ? (
        <Drawer
          id="wallet-information"
          className={userTheme}
          placement={'bottom'}
          closable={false}
          onClose={onClose}
          open={open}
          height={277}
        >
          <div className="relative">
            <div
              onClick={onClose}
              className="absolute right-[0] top-[0] rounded-[100px] bg-[#0000000f] p-[5px] text-[#3c4242cc]"
            >
              <XIcon size={20} />
            </div>
          </div>
          <div className="flex justify-center item-center">
            <div
              style={{
                backgroundColor: color,
              }}
              className="flex justify-center items-center w-[82px] h-[82px] rounded-[100px] text-[45px] mt-[30px]"
            >
              {getTierImage(AllTimeLeaderboardData?.user?.tier) ? (
                <img
                  className="w-[30px] h-[30px]"
                  src={getTierImage(AllTimeLeaderboardData?.user?.tier)}
                />
              ) : (
                emoji
              )}
            </div>
          </div>
          <p className="text-[20px] font-[700] text-center mt-[20px] dark:text-white text-wrap">
            {copyApiBlocked ? walletAddress : ellipseAddress(walletAddress ?? '')}
          </p>
          <p className=" text-center text-[16px] font-[600] text-[#3c424299] dark:text-[#fff9]">
            {BigNumber(data?.formatted ?? '0').toFixed(2)} {data?.symbol}
          </p>
          <div className="flex gap-[10px] mt-[10px]">
            <div
              onClick={handleCopyAddress}
              className="flex flex-col flex-1 items-center justify-center bg-white shadow-[0_4px_12px_rgba(0,0,0,0.1)] p-[6px] rounded-[12px] hover:scale-[1.025] transition dark:bg-[#e0e8ff1a]"
            >
              {copied ? (
                <Check className="dark:text-white" size={18} />
              ) : (
                <Copy className="dark:text-white" size={18} />
              )}
              <span className="text-[12px] text-[#25292E] font-[600] dark:text-white">
                {copied ? `${t('wallet.copied')}!` : t('wallet.copyAddress')}
              </span>
            </div>
            <div
              onClick={handleDisconnect}
              className="flex flex-col flex-1 items-center justify-center bg-white shadow-[0_4px_12px_rgba(0,0,0,0.1)] p-[6px] rounded-[12px] hover:scale-[1.025] transition dark:bg-[#e0e8ff1a]"
            >
              <svg
                className="dark:text-white"
                fill="none"
                height="16"
                viewBox="0 0 18 16"
                width="18"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M2.67834 15.5908H9.99963C11.5514 15.5908 12.399 14.7432 12.399 13.1777V10.2656H10.6354V12.9863C10.6354 13.5332 10.3688 13.8271 9.78772 13.8271H2.89026C2.3092 13.8271 2.0426 13.5332 2.0426 12.9863V3.15625C2.0426 2.60254 2.3092 2.30859 2.89026 2.30859H9.78772C10.3688 2.30859 10.6354 2.60254 10.6354 3.15625V5.89746H12.399V2.95801C12.399 1.39941 11.5514 0.544922 9.99963 0.544922H2.67834C1.12659 0.544922 0.278931 1.39941 0.278931 2.95801V13.1777C0.278931 14.7432 1.12659 15.5908 2.67834 15.5908ZM7.43616 8.85059H14.0875L15.0924 8.78906L14.566 9.14453L13.6842 9.96484C13.5406 10.1016 13.4586 10.2861 13.4586 10.4844C13.4586 10.8398 13.7321 11.168 14.1217 11.168C14.3199 11.168 14.4635 11.0928 14.6002 10.9561L16.7809 8.68652C16.986 8.48145 17.0543 8.27637 17.0543 8.06445C17.0543 7.85254 16.986 7.64746 16.7809 7.43555L14.6002 5.17285C14.4635 5.03613 14.3199 4.9541 14.1217 4.9541C13.7321 4.9541 13.4586 5.27539 13.4586 5.6377C13.4586 5.83594 13.5406 6.02734 13.6842 6.15723L14.566 6.98438L15.0924 7.33984L14.0875 7.27148H7.43616C7.01917 7.27148 6.65686 7.62012 6.65686 8.06445C6.65686 8.50195 7.01917 8.85059 7.43616 8.85059Z"
                  fill="currentColor"
                ></path>
              </svg>
              <span className="text-[12px] text-[#25292E] font-[600] dark:text-white">
                {t('wallet.disconnect')}
              </span>
            </div>
          </div>
        </Drawer>
      ) : (
        <Modal
          className={`wallet-information-modal ${userTheme}`}
          centered
          open={open}
          onClose={onClose}
          onCancel={onClose}
          closable={false}
          footer={false}
          width={360}
          height={240}
        >
          <div className="relative">
            <div
              onClick={onClose}
              className="absolute right-[0] top-[0] rounded-[100px] bg-[#0000000f] dark:bg-[#ffffff14] p-[5px] text-[#3c4242cc] cursor-pointer"
            >
              <XIcon width={14} height={14} className="dark:text-[#e0e8ff99]" size={20} />
            </div>
          </div>
          <div className="flex justify-center item-center">
            <div
              style={{
                backgroundColor: !getTierImage(AllTimeLeaderboardData?.user?.tier)
                  ? color
                  : undefined,
              }}
              className="flex justify-center items-center w-[74px] h-[74px] rounded-[100px] text-[45px] mt-[6px]"
            >
              {getTierImage(AllTimeLeaderboardData?.user?.tier) ? (
                <img
                  className="w-[60px] h-[60px]"
                  src={getTierImage(AllTimeLeaderboardData?.user?.tier)}
                />
              ) : (
                emoji
              )}
            </div>
          </div>
          <p className="text-[16px] font-[700] text-center mt-[20px] lg:mt-[10px] dark:text-white">
            {copyApiBlocked ? walletAddress : ellipseAddress(walletAddress ?? '')}
          </p>
          <p className=" text-center text-[16px] font-[600] text-[#3c424299] dark:text-[#fff9]">
            {BigNumber(data?.formatted ?? '0').toFixed(2)} {data?.symbol}
          </p>
          <div className="flex gap-[10px] mt-[10px]">
            <div
              onClick={handleCopyAddress}
              className="flex flex-col flex-1 items-center justify-center bg-white shadow-[0_4px_12px_rgba(0,0,0,0.1)] p-[6px] rounded-[12px] hover:scale-[1.025] transition dark:bg-[#e0e8ff1a] cursor-pointer"
            >
              {copied ? (
                <Check className="dark:text-white" size={18} />
              ) : (
                <Copy className="dark:text-white" size={18} />
              )}
              <span className="text-[12px] text-[#25292E] font-[600] dark:text-white">
                {copied ? `${t('wallet.copied')}!` : t('wallet.copyAddress')}
              </span>
            </div>
            <div
              onClick={handleDisconnect}
              className="flex flex-col flex-1 items-center justify-center bg-white shadow-[0_4px_12px_rgba(0,0,0,0.1)] p-[6px] rounded-[12px] hover:scale-[1.025] transition dark:bg-[#e0e8ff1a] cursor-pointer"
            >
              <svg
                className="dark:text-white"
                fill="none"
                height="16"
                viewBox="0 0 18 16"
                width="18"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M2.67834 15.5908H9.99963C11.5514 15.5908 12.399 14.7432 12.399 13.1777V10.2656H10.6354V12.9863C10.6354 13.5332 10.3688 13.8271 9.78772 13.8271H2.89026C2.3092 13.8271 2.0426 13.5332 2.0426 12.9863V3.15625C2.0426 2.60254 2.3092 2.30859 2.89026 2.30859H9.78772C10.3688 2.30859 10.6354 2.60254 10.6354 3.15625V5.89746H12.399V2.95801C12.399 1.39941 11.5514 0.544922 9.99963 0.544922H2.67834C1.12659 0.544922 0.278931 1.39941 0.278931 2.95801V13.1777C0.278931 14.7432 1.12659 15.5908 2.67834 15.5908ZM7.43616 8.85059H14.0875L15.0924 8.78906L14.566 9.14453L13.6842 9.96484C13.5406 10.1016 13.4586 10.2861 13.4586 10.4844C13.4586 10.8398 13.7321 11.168 14.1217 11.168C14.3199 11.168 14.4635 11.0928 14.6002 10.9561L16.7809 8.68652C16.986 8.48145 17.0543 8.27637 17.0543 8.06445C17.0543 7.85254 16.986 7.64746 16.7809 7.43555L14.6002 5.17285C14.4635 5.03613 14.3199 4.9541 14.1217 4.9541C13.7321 4.9541 13.4586 5.27539 13.4586 5.6377C13.4586 5.83594 13.5406 6.02734 13.6842 6.15723L14.566 6.98438L15.0924 7.33984L14.0875 7.27148H7.43616C7.01917 7.27148 6.65686 7.62012 6.65686 8.06445C6.65686 8.50195 7.01917 8.85059 7.43616 8.85059Z"
                  fill="currentColor"
                ></path>
              </svg>
              <span className="text-[12px] text-[#25292E] font-[600] dark:text-white">
                {t('wallet.disconnect')}
              </span>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

export default WalletInformationModal;
